import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowLeft, CheckCircle, Loader2, AlertTriangle } from "lucide-react";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reset failed");
      setDone(true);
      toast.success("Password reset — you can sign in now");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1A4A] via-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {!token ? (
          <div className="text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="text-xl font-heading font-bold text-slate-900">Invalid reset link</h1>
            <p className="text-sm text-slate-600">This link is missing its token. Please request a new reset email.</p>
            <Link to="/forgot-password" className="text-sm text-[#1E3A8A] hover:underline font-medium">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-4" data-testid="reset-done">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h1 className="text-xl font-heading font-bold text-slate-900">Password reset!</h1>
            <p className="text-sm text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-heading font-bold text-slate-900 mb-1">Choose a new password</h1>
            <p className="text-sm text-slate-500 mb-6">Enter a new password for your account.</p>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4" data-testid="reset-error">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-600">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-light pl-10" placeholder="At least 8 characters" required minLength={8} data-testid="reset-password-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-light pl-10" placeholder="Re-enter password" required minLength={8} data-testid="reset-confirm-input" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full btn-primary h-11 flex items-center justify-center gap-2" data-testid="reset-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
              </Button>
            </form>
            <div className="text-center mt-6">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline font-medium">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
